import React, { useState } from 'react';
import ScheduleEditor from './ScheduleEditor';
import ConfirmDialog from './ConfirmDialog';
import SVGColorBlock from './SVGColorBlock';
import { Clock, Calendar as CalIcon, Edit, Trash2, Repeat, Sun } from 'lucide-react';
import { RRule } from 'rrule';
import { getScenePreviewColors, getSceneType } from './sceneUtils';
import { useCurrentSystem } from './context/SystemContext';
import {
	isAstroSchedule,
	formatAstroLabel,
	formatTimeInTz,
	getSystemTimezone,
} from './astroUtils';

function ScheduleCard({ schedule, editSchedule, deleteSchedule, scenes = [] }) {
	const { currentSystem } = useCurrentSystem();
	const [editing, setEditing] = useState(false);
	const [deleteConfirm, setDeleteConfirm] = useState(false);
	const sceneId = schedule?.scene || schedule?.cycle;
	const linkedScene = sceneId ? scenes.find((s) => s.uid === sceneId) : null;
	const previewColors = getScenePreviewColors(linkedScene);
	const isCycle = linkedScene && getSceneType(linkedScene) === 'cycle';
	const isAstro = isAstroSchedule(schedule);
	const systemTz = getSystemTimezone(currentSystem);

	const getRRuleSummary = (sch) => {
		if (sch.repeats === false) return null;
		const ruleField = sch?.r_rule || sch?.rrule;
		if (!ruleField) return null;
		try {
			const raw = ruleField.state || ruleField;
			const f = Number(raw.freq);
			const interval = Number(raw.interval) || 1;
			const byweekday = Array.isArray(raw.byweekday) ? raw.byweekday.length : 0;
			const bymonth = Array.isArray(raw.bymonth) ? raw.bymonth.length : 0;
			if ((f === 5 || f === 4) && interval <= 30 && byweekday === 0 && bymonth === 0) {
				const unit = f === 5 ? 'minute' : 'hour';
				return `Every ${interval} ${unit}${interval > 1 ? 's' : ''}`;
			}
			const opts = { ...raw };
			delete opts.runmode;
			delete opts.dtstart;
			if (Array.isArray(opts.byweekday) && opts.byweekday.length === 0) delete opts.byweekday;
			if (Array.isArray(opts.bymonth) && opts.bymonth.length === 0) delete opts.bymonth;
			if (opts.freq !== undefined) {
				const freqMap = {
					0: RRule.YEARLY,
					1: RRule.MONTHLY,
					2: RRule.WEEKLY,
					3: RRule.DAILY,
					4: RRule.HOURLY,
					5: RRule.MINUTELY,
					6: RRule.SECONDLY,
				};
				opts.freq = freqMap[Number(opts.freq)] || opts.freq;
			}
			if (opts.interval !== undefined) {
				opts.interval = +opts.interval || 1;
			}
			return new RRule(opts).toText();
		} catch (e) {
			return null;
		}
	};

	const rruleText = getRRuleSummary(schedule);
	const isOneShot = schedule?.repeats === false || (!schedule?.r_rule && !schedule?.rrule && schedule?.repeats !== true);

	const performAtDate = schedule?.performAt
		? (schedule.performAt.toDate
			? schedule.performAt.toDate()
			: new Date(schedule.performAt.seconds * 1000))
		: null;

	const resolvedTimeLabel = performAtDate
		? formatTimeInTz(performAtDate, systemTz)
		: 'No time';

	const handleDelete = () => {
		if (deleteSchedule) setDeleteConfirm(true);
	};

	const deleteYes = async () => {
		await deleteSchedule(schedule);
		setDeleteConfirm(false);
	};

	return (
		<div className="border border-gray-200 dark:border-gray-700 rounded-xl p-3 bg-white dark:bg-gray-900 text-sm hover:border-[#880e4f]/50 transition-all">
			<div className="flex justify-between items-start gap-2">
				<div className="min-w-0 flex-1">
					<div className="font-medium flex items-center gap-2 truncate"><CalIcon size={15} className="text-gray-500 dark:text-gray-400 shrink-0" /> <span className="truncate">{schedule?.name}</span></div>
					{isAstro ? (
						<>
							<div className="text-xs mt-1 opacity-80 flex items-center gap-1 ml-6 text-[#880e4f]">
								<Sun size={13} /> {formatAstroLabel(schedule.astroEvent, schedule.astroOffsetMinutes)}
							</div>
							<div className="text-xs mt-0.5 opacity-70 flex items-center gap-1 ml-6">
								<Clock size={13} /> {resolvedTimeLabel}
							</div>
						</>
					) : (
						<div className="text-xs mt-1 opacity-70 flex items-center gap-1 ml-6">
							<Clock size={13} /> {performAtDate ? performAtDate.toLocaleString() : 'No time'}
						</div>
					)}
					{isOneShot && !rruleText && (
						<div className="text-[10px] mt-0.5 opacity-60 ml-6 text-gray-500 dark:text-gray-400">One-time</div>
					)}
					{rruleText && (
						<div className="text-[10px] mt-0.5 opacity-60 ml-6 flex items-center gap-1 text-gray-500 dark:text-gray-400">
							<Repeat size={10} /> {rruleText}
						</div>
					)}
					{linkedScene && (
						<div className="flex items-center gap-2 ml-6 mt-1">
							{previewColors.length > 0 && (
								<SVGColorBlock size={24} data={previewColors} />
							)}
							<span className="text-[10px] text-gray-500 dark:text-gray-400 truncate max-w-[120px]">
								{linkedScene.name}{isCycle ? ' (cycle)' : ''}
							</span>
						</div>
					)}
				</div>
				<div className="flex gap-1 flex-shrink-0">
					<button onClick={() => setEditing(true)} className="p-1.5 text-gray-400 hover:text-[#880e4f] hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"><Edit size={15} /></button>
					{deleteSchedule && (
						<button onClick={handleDelete} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg"><Trash2 size={15} /></button>
					)}
				</div>
			</div>

			{editing && (
				<ScheduleEditor
					open={editing}
					schedule={schedule}
					scenes={scenes}
					doneEdit={() => setEditing(false)}
				/>
			)}

			{deleteConfirm && (
				<ConfirmDialog
					open={deleteConfirm}
					title="Delete schedule?"
					message={`Delete "${schedule?.name}"? This cannot be undone.`}
					yes_text="Delete"
					no_text="Cancel"
					on_yes={deleteYes}
					on_no={() => setDeleteConfirm(false)}
				/>
			)}
		</div>
	);
}

export default ScheduleCard;
